import React from 'react';
import ReactDOM from 'react-dom';
import uuid from 'uuid';
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';
import { trigger } from '@confluence-classic/confluence-event_DO_NOT_USE';
import {
	isDarkFeatureEnabled_DO_NOT_USE,
	FABRIC_PRESENCE_INTEGRATION,
} from '@confluence-classic/confluence-feature-flags';

import FabricAnalyticsListeners from '@atlaskit/analytics-listeners';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { MentionPicker, ContextMentionResource } from '@atlaskit/mention';

import { I18nProvider } from '@confluence/i18n';
import { getAtlassianPeopleProfileUrl, getTeamUrl } from '@confluence/profile';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { CONTEXT_PATH } from '@confluence/named-routes';
import { getOverride } from '@confluence/meta';

import { getRte } from '../../../tech-debt/facades/editor';
import { getLink } from '../../../tech-debt/facades/confluence';

import escapeHtml from './escape-html';
import placeComponent from './placement';

const TEAM_MENTION_TYPE = 'TEAM';
const placementOptions = {
	adjustY: 2,
};

/**
 * Inserts a team or user mention depending on mention.userType: "TEAM" or other value.
 */
function insertUserMentionOrTeamLink(mention) {
	const Link = getLink();

	const escapedName = escapeHtml(mention.name);

	let attrs;
	const isTeamMention = mention.userType === TEAM_MENTION_TYPE;

	if (isTeamMention) {
		attrs = {
			href: getTeamUrl(mention.id, 'tinymce', CONTEXT_PATH),
		};
	} else {
		attrs = {
			href: getAtlassianPeopleProfileUrl(mention.id, null, CONTEXT_PATH),
			accountid: mention.id,
			// This attribute stores what the default text for this link should be.
			// At save time the Editor compares this to the content of the <A> tag and will discard the content if it matches the default.
			// This is how we will avoid storing the full name of the person mentioned in cases where the user hasn't changed that link text manually.
			'data-linked-resource-default-alias': escapedName,
		};
	}

	const linkData = {
		attrs,
		body: {
			html: escapedName,
			text: escapedName,
		},
		classes: [isTeamMention ? '' : 'confluence-link'],
	};

	const mentionLink = Link.fromData(linkData);
	mentionLink.insert();
}

function placePicker(anchor, picker, options) {
	// Defer to allow picker to be rendered so it gets the correct height
	setTimeout(() => {
		placeComponent(anchor, picker, options);
	}, 1);
}

function getResourceProvider(options) {
	const { mentionResource, contextIdentifier } = options || {};
	return contextIdentifier
		? new ContextMentionResource(mentionResource, {
				...contextIdentifier,
				sessionId: uuid(),
			})
		: mentionResource;
}

/**
 * options: {
 *   anchor: element to anchor picker to,
 *   update: callback when item "selected".
 *           parameter is the data representing the selected item. in this case a mention.
 * }
 */
export default class PickerAdapter {
	constructor(options) {
		this._mentionSelected = this._mentionSelected.bind(this);
		this._setPickerRef = this._setPickerRef.bind(this);

		this.options = options;
		this.resourceProvider = getResourceProvider(options);

		if (isDarkFeatureEnabled_DO_NOT_USE(FABRIC_PRESENCE_INTEGRATION)) {
			this.presenceProvider = options.presenceResource;
		}

		this.$mentionPickerContainer = $('<div/>');
		this.mentionPickerContainer = this.$mentionPickerContainer[0];
		this.query = options.query;

		// Doing manual positioning here, not using ak support, as it has
		// some overlay/z-index issues with the editor title.
		this.$mentionPickerContainer.css({
			position: 'absolute',
			'z-index': 10000,
			display: 'none',
		});

		$('body').append(this.mentionPickerContainer);

		this._renderPicker();

		this.resourceProvider.subscribe(
			'@atlaskit/mentions',
			(mentions) => {
				if (mentions && mentions.length > 0) {
					this.reposition();
				}
			},
			() => {
				this.reposition();
			},
		);
	}

	_setPickerRef(ref) {
		this.mentionPickerRef = ref;
	}

	_mentionSelected = (mention) => {
		if (mention) {
			this.options.update(mention);
		} else {
			// If nothing selected, insert current selection
			this.options.die();
		}
	};

	_getAnalyticsContext = () => ({
		attributes: { editorType: 'tinyMCE' },
		fabricElementsCtx: { editorType: 'tinyMCE' },
	});

	async _renderPicker() {
		const reactMentionPicker = (
			// Team result items in MentionPicker are rendered by the legacy editor and it uses FormattedMessage of react-intl
			// So we need to wrap MentionPicker in IntlProvider so that MentionPicker can be rendered inside the legacy editor
			<I18nProvider>
				<FabricAnalyticsListeners client={getAnalyticsWebClient()}>
					<AnalyticsContext data={this._getAnalyticsContext()}>
						<MentionPicker
							resourceProvider={this.resourceProvider}
							presenceProvider={this.presenceProvider}
							query={this.query}
							onSelection={this._mentionSelected}
							ref={this._setPickerRef}
							createTeamPath={`${CONTEXT_PATH}/people/search#createTeam`}
						/>
					</AnalyticsContext>
				</FabricAnalyticsListeners>
			</I18nProvider>
		);
		// eslint-disable-next-line react/no-deprecated
		ReactDOM.render(reactMentionPicker, this.mentionPickerContainer);
	}

	update(mention) {
		const isTeamMention = mention.userType === TEAM_MENTION_TYPE && !!mention.context;
		trigger('mention-autocomplete-on-select', {
			mentioneeId: mention.id, // can be team id or user id
			contentId: getOverride('content-id'),
			queryLength: (this.query || '').length,
			// keyboardKey: there is no this kind of data in `mention`
			userType: mention.userType,
			memberCount: isTeamMention ? mention.context.memberCount : null,
			includesYou: isTeamMention ? mention.context.includesYou : null,
		});
		insertUserMentionOrTeamLink(mention);

		if (isTeamMention) {
			const editor = getRte().getEditor();
			const teamMembers = mention.context.members;
			const len = teamMembers.length;

			for (let index = 0; index < len; index++) {
				if (index === 0) {
					editor.execCommand('mceInsertContent', false, ' (');
				}
				insertUserMentionOrTeamLink(teamMembers[index]);

				if (index + 1 === len) {
					editor.execCommand('mceInsertContent', false, ')');
				} else {
					editor.execCommand('mceInsertContent', false, ', ');
				}
			}
		}
	}

	onTextChange(text) {
		this.query = text;
		this._renderPicker();
	}

	isDataValid() {
		return true;
	}

	die() {
		// eslint-disable-next-line react/no-deprecated
		ReactDOM.unmountComponentAtNode(this.mentionPickerContainer);
		this.$mentionPickerContainer.remove();
		this.resourceProvider.unsubscribe('@atlaskit/mentions');
	}

	onKeyUp() {
		this.mentionPickerRef.selectPrevious();
	}

	onKeyDown() {
		this.mentionPickerRef.selectNext();
	}

	onKeyEnter() {
		this.mentionPickerRef.chooseCurrentSelection();
	}

	onKeyTab() {
		this.mentionPickerRef.chooseCurrentSelection();
	}

	reposition() {
		placePicker(this.options.anchor, this.$mentionPickerContainer, placementOptions);
	}
}
